import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { profileBioSchema, customTextSchema, organizationSchema, phoneSchema } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import { Loader2, Plus, X, Download, Sparkles, Upload, Edit, RefreshCw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { domainCategoriesMap, getCategoriesForDomain, getSubcategoriesForCategory } from "@/data/domainCategoriesMap";
import { ImageRegionSelector } from "@/components/ImageRegionSelector";
const profileSchema = z.object({
  name: z.string().min(1, "Name is required").max(100),
  title: z.string().min(1, "Title is required").max(200),
  organization: organizationSchema,
  phone: phoneSchema,
  linkedinBio: profileBioSchema,
  linkedinLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  industries: z.array(z.string()).min(1, "Select at least one industry")
});
type ProfileFormValues = z.infer<typeof profileSchema>;
const industryOptions = ["Construction", "Engineering", "Architecture", "Real Estate", "Technology", "Manufacturing", "Energy", "Infrastructure", "Transportation", "Utilities", "Other"];
export default function CreateMySPAIProfile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const { profile } = useProfile();
  
  // Early return if profile already exists - prevent component from mounting
  if (profile?.username) {
    return null;
  }
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [selectedDomains, setSelectedDomains] = useState<Array<{
    domain: string;
    category: string;
    subcategory: string;
  }>>([]);
  const [originalPrompt, setOriginalPrompt] = useState<string>("");
  const [refinementFeedback, setRefinementFeedback] = useState<string>("");
  const [isRefining, setIsRefining] = useState(false);
  const [iterationCount, setIterationCount] = useState(0);
  const [currentDomain, setCurrentDomain] = useState("");
  const [currentCategory, setCurrentCategory] = useState("");
  const [currentSubcategory, setCurrentSubcategory] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [customSubcategory, setCustomSubcategory] = useState("");
  const [imageSource, setImageSource] = useState<"generated" | "uploaded">("generated");
  const [isEditingRegion, setIsEditingRegion] = useState(false);
  const [isApplyingEdit, setIsApplyingEdit] = useState(false);
  const [editHistory, setEditHistory] = useState<string[]>([]);
  const [customIndustry, setCustomIndustry] = useState("");
  const [existingGibNifty, setExistingGibNifty] = useState<string | null>(null);
  const [showExistingOptions, setShowExistingOptions] = useState(false);
  
  // Get organization from URL parameters
  const organizationFromUrl = searchParams.get('organization');
  
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
      title: "",
      organization: organizationFromUrl || "",
      phone: "",
      linkedinBio: "",
      linkedinLink: "",
      industries: []
    }
  });


  // Load existing GibNifty from static profile mapping
  useEffect(() => {
    const loadExistingProfileData = async () => {
      if (!user?.email) return;
      
      const { data: staticProfile } = await supabase
        .from('static_profile_mappings')
        .select('gibnifty_url, full_name, title')
        .eq('email', user.email)
        .maybeSingle();
      
      if (staticProfile) {
        // Load existing GibNifty
        if (staticProfile.gibnifty_url) {
          setExistingGibNifty(staticProfile.gibnifty_url);
          setGeneratedImage(staticProfile.gibnifty_url);
          setImageSource("uploaded");
          setShowExistingOptions(true);
        }
        
        // Pre-populate form fields
        if (staticProfile.full_name) {
          form.setValue('name', staticProfile.full_name);
        }
        if (staticProfile.title) {
          form.setValue('title', staticProfile.title);
        }
      }
    };
    
    loadExistingProfileData();
  }, [user, form]);
  
  const availableDomains = Object.keys(domainCategoriesMap);
  const availableCategories = currentDomain ? getCategoriesForDomain(currentDomain) : [];
  const availableSubcategories = currentDomain && currentCategory ? getSubcategoriesForCategory(currentDomain, currentCategory) : [];
  const addDomainExpertise = () => {
    const finalDomain = currentDomain === "Other" ? customDomain : currentDomain;
    const finalCategory = currentCategory === "Other" ? customCategory : currentCategory;
    const finalSubcategory = currentSubcategory === "Other" ? customSubcategory : currentSubcategory;
    if (finalDomain && finalCategory && finalSubcategory) {
      // Validate custom inputs
      try {
        if (currentDomain === "Other") customTextSchema.parse(customDomain);
        if (currentCategory === "Other") customTextSchema.parse(customCategory);
        if (currentSubcategory === "Other") customTextSchema.parse(customSubcategory);
      } catch (error) {
        if (error instanceof z.ZodError) {
          toast({
            title: "Validation Error",
            description: error.issues[0].message,
            variant: "destructive"
          });
          return;
        }
      }
      const exists = selectedDomains.some(d => d.domain === finalDomain && d.category === finalCategory && d.subcategory === finalSubcategory);
      if (!exists) {
        setSelectedDomains([...selectedDomains, {
          domain: finalDomain,
          category: finalCategory,
          subcategory: finalSubcategory
        }]);
        setCurrentDomain("");
        setCurrentCategory("");
        setCurrentSubcategory("");
        setCustomDomain("");
        setCustomCategory("");
        setCustomSubcategory("");
      }
    }
  };
  const removeDomainExpertise = (index: number) => {
    setSelectedDomains(selectedDomains.filter((_, i) => i !== index));
  };
  const downloadGibNifty = () => {
    if (!generatedImage) return;
    try {
      // Convert base64 to blob
      const base64Data = generatedImage.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'image/png'
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const userName = form.getValues().name || 'user';
      link.href = url;
      link.download = `gibnifty-${userName.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        title: "Download started",
        description: "Your GibNifty image is being downloaded"
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "Failed to download the image",
        variant: "destructive"
      });
    }
  };
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG, JPG, or WEBP image",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive"
      });
      return;
    }
    try {
      const reader = new FileReader();
      reader.onload = e => {
        const base64 = e.target?.result as string;
        if (generatedImage) {
          setEditHistory([...editHistory, generatedImage]);
        }
        setGeneratedImage(base64);
        setImageSource("uploaded");
        setIterationCount(0);
        toast({
          title: "Image uploaded",
          description: "Your custom GibNifty has been uploaded successfully"
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload the image",
        variant: "destructive"
      });
    }
  };
  const handleRegionEdit = async (regionDescription: string, editInstructions: string) => {
    if (!user || !generatedImage) return;
    setIsApplyingEdit(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('edit-gibnifty-region', {
        body: {
          imageUrl: generatedImage,
          regionDescription,
          editInstructions
        }
      });
      if (error) throw error;
      if (data?.image) {
        setEditHistory([...editHistory, generatedImage]);
        setGeneratedImage(data.image);
        setIterationCount(prev => prev + 1);
        setIsEditingRegion(false);
        toast({
          title: "Region edited successfully!",
          description: "Your GibNifty has been updated"
        });
      }
    } catch (error: any) {
      console.error("Error editing region:", error);
      toast({
        title: "Edit failed",
        description: error.message || "Failed to edit the selected region",
        variant: "destructive"
      });
    } finally {
      setIsApplyingEdit(false);
    }
  };
  const refineGibNifty = async () => {
    if (!user || !originalPrompt || !refinementFeedback.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide feedback for refinement",
        variant: "destructive"
      });
      return;
    }
    setIsRefining(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('refine-gibnifty', {
        body: {
          originalPrompt,
          feedback: refinementFeedback.trim()
        }
      });
      if (error) throw error;
      if (data?.image) {
        setEditHistory([...editHistory, generatedImage!]);
        setGeneratedImage(data.image);
        setIterationCount(prev => prev + 1);
        setRefinementFeedback("");
        toast({
          title: "GibNifty refined!",
          description: "Your profile picture has been updated with your feedback"
        });
      }
    } catch (error: any) {
      console.error("Error refining GibNifty:", error);
      toast({
        title: "Refinement failed",
        description: error.message || "Failed to refine GibNifty",
        variant: "destructive"
      });
    } finally {
      setIsRefining(false);
    }
  };
  const generateGibNifty = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to generate your GibNifty",
        variant: "destructive"
      });
      return;
    }
    const values = form.getValues();
    if (!values.name || !values.title) {
      toast({
        title: "Missing information",
        description: "Please fill in your name and title before generating",
        variant: "destructive"
      });
      return;
    }
    setIsGenerating(true);
    try {
      // Build the GibNifty prompt based on the grounding document
      const domainsList = selectedDomains.map(d => `${d.domain} - ${d.category}: ${d.subcategory}`).join(", ");

      // Handle custom industry
      let industriesList = values.industries.filter(i => i !== "Other").join(", ");
      if (values.industries.includes("Other") && customIndustry.trim()) {
        industriesList += (industriesList ? ", " : "") + customIndustry.trim();
      }

      // Sanitize and limit prompt length
      const safeBio = values.linkedinBio.substring(0, 500);
      const safeDomains = domainsList.substring(0, 200);
      const prompt = `Create a GibNifty for ${values.name}. 

PROFILE DETAILS:
• Name: ${values.name}
• Title: ${values.title}
• Bio: ${safeBio}
• Expertise: ${safeDomains || "general construction knowledge"}
• Industries: ${industriesList}

Show them as a confident professional in their element, with props and setting that tell their unique story.`.substring(0, 2000);

      // Store the original prompt for refinements
      setOriginalPrompt(prompt);
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-gibnifty', {
        body: {
          prompt
        }
      });
      if (error) throw error;
      if (data?.image) {
        setGeneratedImage(data.image);
        setIterationCount(1);
        toast({
          title: "GibNifty generated!",
          description: "Your profile picture has been created successfully"
        });
      }
    } catch (error: any) {
      console.error("Error generating GibNifty:", error);
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate GibNifty",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };
  const onSubmit = async (values: ProfileFormValues) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create your profile",
        variant: "destructive"
      });
      return;
    }
    try {
      // Check for static profile mapping
      const { data: staticProfile } = await supabase
        .from('static_profile_mappings')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      // Check for waitlist entry
      const { data: waitlistEntry } = await supabase
        .from('waitlist')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();
      // Generate username from display name
      const generateUsername = (name: string): string => {
        return name
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Remove multiple hyphens
          .trim();
      };

      let username = generateUsername(values.name);
      
      // Check if username exists and append number if needed
      let usernameExists = true;
      let attemptCount = 0;
      let finalUsername = username;

      while (usernameExists && attemptCount < 100) {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', finalUsername)
          .maybeSingle();

        if (!existingProfile) {
          usernameExists = false;
        } else {
          attemptCount++;
          finalUsername = `${username}-${attemptCount}`;
        }
      }

      // Prepare industries array
      let industriesArray = values.industries.filter(i => i !== "Other");
      if (values.industries.includes("Other") && customIndustry.trim()) {
        industriesArray.push(customIndustry.trim());
      }

      // Use static profile GibNifty if available
      const avatarUrl = staticProfile?.gibnifty_url || generatedImage;

      // Update profile
      const {
        error: profileError
      } = await supabase.from('profiles').update({
        display_name: values.name,
        bio: values.linkedinBio,
        avatar_url: avatarUrl,
        title: values.title,
        organization: values.organization,
        phone: values.phone,
        linkedin_url: values.linkedinLink,
        industries: industriesArray,
        username: finalUsername,
        has_completed_onboarding: true,
      }).eq('id', user.id);
      if (profileError) throw profileError;

      // Mark static profile as replaced
      if (staticProfile) {
        await supabase
          .from('static_profile_mappings')
          .update({
            replaced_by_user_id: user.id,
            replaced_at: new Date().toISOString(),
          })
          .eq('id', staticProfile.id);
      }

      // Mark waitlist entry as completed
      if (waitlistEntry) {
        await supabase
          .from('waitlist')
          .update({ completed_profile: true })
          .eq('id', waitlistEntry.id);
      }

      // Check if user already has a channel assignment
      const { data: existingOrg } = await supabase
        .from('user_organizations')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      // Only assign channel if user doesn't have one yet
      if (!existingOrg) {
        // Assign to channel (use static profile channel or default to CURT Channel)
        const channelName = staticProfile?.channel_name || 'CURT Channel';
        const { data: channel } = await supabase
          .from('channels')
          .select('id')
          .eq('name', channelName)
          .maybeSingle();

        if (channel) {
          await supabase.from('user_organizations').insert({
            user_id: user.id,
            channel_id: channel.id,
            organization_name: values.organization,
            is_primary: true,
          });
        }
      }

      // Save domain expertise
      for (const expertise of selectedDomains) {
        const {
          error: expertiseError
        } = await supabase.from('user_expertise').insert({
          user_id: user.id,
          domain_name: expertise.domain,
          category: expertise.category,
          subcategory: expertise.subcategory
        });
        if (expertiseError) throw expertiseError;
      }
      // Check for pending sprint invitation
      const pendingInvitation = sessionStorage.getItem('pendingSprintInvitation');
      
      if (pendingInvitation) {
        sessionStorage.removeItem('pendingSprintInvitation');
        toast({
          title: "Profile created!",
          description: "Now joining your sprint..."
        });
        navigate(`/accept-sprint-invitation/${pendingInvitation}`);
        return;
      }

      toast({
        title: "Profile created!",
        description: "Your mySPAI profile has been created successfully"
      });
      navigate('/creators');
    } catch (error: any) {
      console.error("Error creating profile:", error);
      toast({
        title: "Creation failed",
        description: error.message || "Failed to create profile",
        variant: "destructive"
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Create Your mySPAI Profile</h1>
          <p className="text-lg text-gray-600">
            Set up your profile with expertise domains and generate your unique GibNifty avatar
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Basic Information</h2>
              
              <div className="space-y-6">
                <FormField control={form.control} name="name" render={({
                field
              }) => <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="title" render={({
                field
              }) => <FormItem>
                      <FormLabel>Title/Role *</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Senior Construction Manager" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="organization" render={({
                field
              }) => <FormItem>
                      <FormLabel>Organization *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your organization" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="phone" render={({
                field
              }) => <FormItem>
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your phone number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="linkedinBio" render={({
                field
              }) => <FormItem>
                      <FormLabel>LinkedIn Bio *</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Paste your LinkedIn bio or write a brief professional summary..." className="min-h-[120px]" {...field} />
                      </FormControl>
                      <FormDescription>This helps us create a personalized GibNifty for you</FormDescription>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="linkedinLink" render={({
                field
              }) => <FormItem>
                      <FormLabel>LinkedIn Profile URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://linkedin.com/in/yourprofile" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Industries *</h2>
              
              <FormField control={form.control} name="industries" render={({
              field
            }) => <FormItem>
                    <FormDescription className="mb-4">
                      Select all industries that apply to your experience
                    </FormDescription>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {industryOptions.map(industry => <label key={industry} className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-all ${field.value.includes(industry) ? "border-primary bg-primary/10" : "border-gray-200 hover:border-gray-300"}`}>
                          <input type="checkbox" className="sr-only" checked={field.value.includes(industry)} onChange={e => {
                    const value = field.value || [];
                    if (e.target.checked) {
                      field.onChange([...value, industry]);
                    } else {
                      field.onChange(value.filter(v => v !== industry));
                      if (industry === "Other") {
                        setCustomIndustry("");
                      }
                    }
                  }} />
                          <span className="text-sm font-medium">{industry}</span>
                        </label>)}
                    </div>
                    {field.value.includes("Other") && <div className="mt-4">
                        <label className="block text-sm font-medium mb-2">
                          Specify other industry
                        </label>
                        <Input placeholder="Enter custom industry" value={customIndustry} onChange={e => setCustomIndustry(e.target.value)} maxLength={100} />
                        <p className="text-sm text-muted-foreground mt-1">
                          {customIndustry.length}/100 characters
                        </p>
                      </div>}
                    <FormMessage />
                  </FormItem>} />
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Domains & Expertise</h2>
              
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Domain</label>
                    <Select value={currentDomain} onValueChange={value => {
                    setCurrentDomain(value);
                    setCurrentCategory("");
                    setCurrentSubcategory("");
                    if (value !== "Other") setCustomDomain("");
                  }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select domain" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDomains.map(domain => <SelectItem key={domain} value={domain}>
                            {domain}
                          </SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {currentDomain === "Other" && <Input placeholder="Enter custom domain" value={customDomain} onChange={e => setCustomDomain(e.target.value)} className="mt-2" />}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <Select value={currentCategory} onValueChange={value => {
                    setCurrentCategory(value);
                    setCurrentSubcategory("");
                    if (value !== "Other") setCustomCategory("");
                  }} disabled={!currentDomain}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.map(cat => <SelectItem key={cat.name} value={cat.name}>
                            {cat.name}
                          </SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {currentCategory === "Other" && <Input placeholder="Enter custom category" value={customCategory} onChange={e => setCustomCategory(e.target.value)} className="mt-2" />}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subcategory</label>
                    <Select value={currentSubcategory} onValueChange={value => {
                    setCurrentSubcategory(value);
                    if (value !== "Other") setCustomSubcategory("");
                  }} disabled={!currentCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubcategories.map(sub => <SelectItem key={sub.name} value={sub.name}>
                            {sub.name}
                          </SelectItem>)}
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {currentSubcategory === "Other" && <Input placeholder="Enter custom subcategory" value={customSubcategory} onChange={e => setCustomSubcategory(e.target.value)} className="mt-2" />}
                  </div>
                </div>

                <Button type="button" onClick={addDomainExpertise} disabled={!currentDomain || !currentCategory || !currentSubcategory || currentDomain === "Other" && !customDomain || currentCategory === "Other" && !customCategory || currentSubcategory === "Other" && !customSubcategory} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Domain Expertise
                </Button>
              </div>

              {selectedDomains.length > 0 && <div className="space-y-2">
                  <h3 className="font-semibold mb-3">Selected Expertise:</h3>
                  {selectedDomains.map((expertise, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{expertise.domain}</p>
                        <p className="text-sm text-gray-600">{expertise.category} → {expertise.subcategory}</p>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeDomainExpertise(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>)}
                </div>}
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6">Generate Your GibNifty</h2>
              
              {/* Show existing GibNifty if available */}
              {existingGibNifty && showExistingOptions && !generatedImage && (
                <div className="space-y-4 p-6 bg-blue-50 rounded-lg border-2 border-blue-200 mb-6">
                  <h3 className="text-xl font-semibold text-center">Your Default GibNifty</h3>
                  <p className="text-sm text-gray-600 text-center">
                    We found an existing GibNifty for you. You can keep it or create a new one.
                  </p>
                  <img 
                    src={existingGibNifty} 
                    alt="Your existing GibNifty" 
                    className="w-64 h-64 rounded-lg mx-auto border-4 border-blue-300 object-cover"
                  />
                  <div className="flex gap-4 justify-center">
                    <Button 
                      type="button"
                      variant="default"
                      size="lg"
                      onClick={() => {
                        setGeneratedImage(existingGibNifty);
                        setImageSource("uploaded");
                        toast({ title: "✓ Using your existing GibNifty" });
                      }}
                    >
                      Keep This GibNifty
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setShowExistingOptions(false);
                        setGeneratedImage(null);
                      }}
                    >
                      Create New GibNifty
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="text-center space-y-4">
                {generatedImage && (!existingGibNifty || !showExistingOptions) ? <div className="space-y-6">
                    {!isEditingRegion ? <>
                        <div className="space-y-2">
                          <div className="relative inline-block">
                            <img src={generatedImage} alt="Your GibNifty" className="w-64 h-64 mx-auto rounded-lg object-cover" />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="absolute top-2 right-2 bg-background/80 backdrop-blur-sm"
                              onClick={() => {
                                setGeneratedImage(null);
                                setIterationCount(0);
                                setOriginalPrompt("");
                                setEditHistory([]);
                              }}
                              tooltip="Change image"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </div>
                          {iterationCount > 0 && <p className="text-sm text-gray-500">
                              {imageSource === "uploaded" ? "Uploaded" : `Version ${iterationCount}`}
                              {editHistory.length > 0 && ` (${editHistory.length + 1} total edits)`}
                            </p>}
                        </div>

                        <div className="flex gap-3 justify-center flex-wrap">
                          <Button type="button" onClick={downloadGibNifty} variant="outline">
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </Button>
                          <Button type="button" onClick={() => setIsEditingRegion(true)} variant="outline">
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Region
                          </Button>
                          <Button type="button" onClick={generateGibNifty} disabled={isGenerating} variant="outline">
                            {isGenerating ? <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Regenerating...
                              </> : "Regenerate"}
                          </Button>
                        </div>

                        <Collapsible>
                          <CollapsibleTrigger asChild>
                            
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4 space-y-4">
                            <div className="text-left">
                              <label className="block text-sm font-medium mb-2">
                                How would you like to adjust your entire GibNifty?
                              </label>
                              <Textarea placeholder="e.g., Make the owl wear glasses, add a hard hat, use warmer colors..." value={refinementFeedback} onChange={e => setRefinementFeedback(e.target.value)} className="min-h-[100px]" maxLength={500} />
                              <p className="text-sm text-gray-500 mt-1">
                                {refinementFeedback.length}/500 characters
                              </p>
                            </div>
                            <Button type="button" onClick={refineGibNifty} disabled={isRefining || !refinementFeedback.trim()} className="w-full">
                              {isRefining ? <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Refining...
                                </> : <>
                                  <Sparkles className="mr-2 h-4 w-4" />
                                  Refine Entire Image
                                </>}
                            </Button>
                          </CollapsibleContent>
                        </Collapsible>
                      </> : <ImageRegionSelector imageUrl={generatedImage} onEdit={handleRegionEdit} onCancel={() => setIsEditingRegion(false)} isEditing={isApplyingEdit} />}
                  </div> : <div className="space-y-4">
                    <p className="text-gray-600">
                      Generate your unique GibNifty avatar or upload your own
                    </p>
                    
                    <div className="flex gap-3 justify-center flex-wrap">
                      <Button type="button" onClick={generateGibNifty} disabled={isGenerating} size="lg">
                        {isGenerating ? <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </> : "Generate My GibNifty"}
                      </Button>

                      <div className="relative">
                        <input type="file" id="gibnifty-upload" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleImageUpload} className="hidden" />
                        <Button type="button" variant="outline" size="lg" onClick={() => document.getElementById('gibnifty-upload')?.click()}>
                          <Upload className="mr-2 h-4 w-4" />
                          Upload Your Own
                        </Button>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground">
                      Supported formats: PNG, JPG, WEBP (max 5MB)
                    </p>
                  </div>}
              </div>
            </Card>

            <div className="flex justify-center">
              <Button type="submit" size="lg" className="px-12">
                Create Profile
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>;
}